// R-RX-14 — the medication card's PRODUCT SLOT: the first on-card control a
// medication has ever had on the web (L52).
//
// A dictated name we anchored to one RxNorm identity, whose product did not
// resolve, is staged as a card with the slot EMPTY and the choices on it — the
// practice's own template first, then the products at the strength that was
// dictated, then the strengths the drug actually comes in, every one of them
// labelled with its form (O112).
//
// PR-CF-4: a card's open slot is an OPEN QUESTION, and an open question has ONE
// door — `/pick`, naming the conversation, the question and the option (R-CF-7
// SIGNED). The unresolved card's buttons used to be a second door
// (`set-med-product` on `/order-edit`), which answered a card that was not the
// head question and left a different row than the chips did whenever a second
// question was queued (F0-8). They now hand the view's OWN option to the page's
// pick handler — and only on the card whose order the open question is about;
// another card with an open slot waits for it. The same parts serve a referral
// whose recipient is being chosen (`RecipientSlot`, below).
//
// O113 puts the same control on a RESOLVED card in one case: the practice holds
// a template of the same drug at another strength. That card is confirmable, so
// its control is a neutral outline — not the danger chip — and it reads as what
// it is, a one-tap switch of product and dose together. It is not an open
// question, and it keeps its own `set-med-product` action.
import React from 'react';

// The lab rail's palette, so a medication that needs a decision looks like
// every other card that needs one (order-doc/lab-order-card.jsx TONE_CHIP).
const DANGER = { bg: '#fef2f2', fg: '#b91c1c', border: '#fecaca' };

// R/V-8 rejected "a slot that can never be filled", and the chip is that slot's
// invitation: a product-less card the resolver could not anchor (a `_clarify`
// row that reached review) carries `productStatus 'unresolved'` and NO options,
// and the chip invited a pick the slot cannot offer. The clarify text is that
// card's answer, not a button. A referral whose recipient is being chosen
// (PR-CF-4) wears the same chip, for the same reason, only while it has options.
export function showPickChip(card) {
  if (!card) return false;
  if (card.type === 'medication') {
    return card.productStatus === 'unresolved' && Array.isArray(card.productOptions) && card.productOptions.length > 0;
  }
  if (card.type === 'referral') {
    return card.recipientStatus === 'choose' && Array.isArray(card.recipientOptions) && card.recipientOptions.length > 0;
  }
  return false;
}

// The title chip on an unresolved card: the card SAYS it is not orderable yet,
// beside the words the provider actually said — and what it is waiting for.
export function PickProductChip({ card } = {}) {
  return (
    <span
      className="orderchat-doc-chip orderchat-doc-chip--danger"
      style={{
        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
        background: DANGER.bg, color: DANGER.fg, border: `1px solid ${DANGER.border}`,
      }}
    >
      {card && card.type === 'referral' ? 'Pick a recipient' : 'Pick a product'}
    </span>
  );
}

// The button text for O113's switch. The control already says "your template",
// so the label's own parenthetical would say it twice.
export function switchLabel(label) {
  return `Switch to your template — ${String(label || '').replace('(your template, qty ', '(qty ')}`;
}

// The note a strength-changing pick leaves behind (R/V-5): what the provider
// SAID stays on the card until they edit the dose themselves. The readback
// carries the same sentence.
export function dictatedDoseNote(card) {
  const said = card && card.dictated && card.dictated.dose;
  if (!said || !card.dose || said === card.dose) return null;
  return `Dose set to the product's strength — you said ${said}; put the count per dose in the directions.`;
}

// O114: the option belongs to the drug's OTHER routes — an eye ointment, a
// skin cream — and lives behind the primary list, not among it. An option with
// no group is a row staged before this deploy and reads as primary.
export function isSecondaryOption(opt) {
  return !!(opt && opt.group === 'secondary');
}

export function MedProductSlot({ card, onEdit, question = null, orderIndex = null, onPick }) {
  if (!card || card.type !== 'medication') return null;
  const options = Array.isArray(card.productOptions) ? card.productOptions : [];
  const unresolved = card.productStatus === 'unresolved';
  const note = unresolved ? null : dictatedDoseNote(card);
  if (!options.length && !note) return null;
  // O113's RESOLVED-card switch — not an open question; its own action.
  const pick = (id) => onEdit && onEdit({ action: 'set-med-product', payload: { medicationId: id } });
  // The open question this card answers, when it is THIS card's (PR-CF-4).
  const head = unresolved ? openSlotQuestion(question, orderIndex, card) : null;
  const primary = options.filter((o) => !isSecondaryOption(o));
  const secondary = options.filter(isSecondaryOption);
  const button = (opt) => {
    const answer = unresolved ? answerFor(head, opt.id) : null;
    const waiting = unresolved && !answer;
    return (
      <button
        key={opt.id}
        type="button"
        disabled={waiting}
        onClick={() => {
          if (!unresolved) pick(opt.id);
          else if (answer && onPick) onPick(answer);
        }}
        style={{
          padding: '6px 10px', fontSize: 12, fontWeight: 600, textAlign: 'left',
          color: unresolved ? DANGER.fg : '#374151',
          background: '#fff',
          border: `1px solid ${unresolved ? DANGER.border : '#d1d5db'}`,
          borderRadius: 8, cursor: waiting ? 'not-allowed' : 'pointer', opacity: waiting ? 0.6 : 1,
        }}
      >
        {unresolved ? opt.label : switchLabel(opt.label)}
      </button>
    );
  };
  return (
    <div className="orderchat-med-product-slot" style={{ marginTop: 8 }}>
      {note && (
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: options.length ? 6 : 0 }}>{note}</div>
      )}
      {unresolved && options.length > 0 && (
        <div style={{ fontSize: 12, color: '#b91c1c', marginBottom: 6 }}>
          No product chosen yet — pick one:
        </div>
      )}
      {unresolved && options.length > 0 && !head && <SlotWaitNote />}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {primary.map(button)}
      </div>
      {/* The drug's other routes, behind a native disclosure so the primary
          list is what the card shows — the same buttons, the same answer, the
          same tone; open it and they are there (O114). */}
      {secondary.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ fontSize: 12, color: '#6b7280', cursor: 'pointer' }}>
            {`Other routes (${secondary.length})`}
          </summary>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {secondary.map(button)}
          </div>
        </details>
      )}
    </div>
  );
}

// ── The parts both slots share (PR-CF-4) ──────────────────────────────────────

// Said on a card whose slot is open while the OPEN question is another card's:
// the queue is answered in order, so its buttons wait (veto line (xxxix)).
export const SLOT_WAIT_NOTE = 'Answer the question above first';
function SlotWaitNote() {
  return <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>{SLOT_WAIT_NOTE}</div>;
}

// The view's open question when it is about THIS card's order and is the kind
// this card's slot holds — else null (the card waits). `question` is the LIVE
// view of the card's own conversation (R-CF-7 / D3).
const SLOT_KIND = { medication: 'med', referral: 'recipient' };
export function openSlotQuestion(question, orderIndex, card) {
  if (!question || !card || orderIndex == null || question.orderIndex !== orderIndex) return null;
  if (!Array.isArray(question.options)) return null;
  return SLOT_KIND[card.type] && question.kind === SLOT_KIND[card.type] ? question : null;
}

// The question's own option for the id a card button shows — the SERVER's
// option, which is what the pick names. A key that names no option, or two, is
// no answer (the route's exactly-one rule, mirrored).
export function answerFor(question, id) {
  if (!question || id == null || !Array.isArray(question.options)) return null;
  const hits = question.options.filter((o) => o && o.optionId != null && String(o.optionId) === String(id));
  return hits.length === 1 ? hits[0] : null;
}

// PR-CF-4 — a referral whose spoken recipient matched several contacts: the
// choices ON the card, each answered through the same pick door as the chips.
// The labels are the server's (discriminator-first, distinct within 32
// characters, "New contact …" last when a fax or email was given).
export function RecipientSlot({ card, question = null, orderIndex = null, onPick }) {
  if (!card || card.type !== 'referral' || card.recipientStatus !== 'choose') return null;
  const options = Array.isArray(card.recipientOptions) ? card.recipientOptions : [];
  if (!options.length) return null;
  const head = openSlotQuestion(question, orderIndex, card);
  return (
    <div className="orderchat-recipient-slot" style={{ marginTop: 8 }}>
      {!head && <SlotWaitNote />}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map((opt) => {
          const answer = answerFor(head, opt.key);
          return (
            <button
              key={opt.key}
              type="button"
              disabled={!answer}
              onClick={() => { if (answer && onPick) onPick(answer); }}
              style={{
                padding: '6px 10px', fontSize: 12, fontWeight: 600, textAlign: 'left',
                color: DANGER.fg, background: '#fff', border: `1px solid ${DANGER.border}`,
                borderRadius: 8, cursor: answer ? 'pointer' : 'not-allowed', opacity: answer ? 1 : 0.6,
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
